import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAbortableLoad } from '../hooks/useAbortableLoad';
import { tienePermiso } from '../utils/navPermisos';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Plus, Edit, Trash2, Loader2, ShieldCheck, AlertCircle, Lock, Eye,
} from 'lucide-react';

const emptyMatrix = (modulos) => {
  const m = {};
  (modulos || []).forEach((mod) => {
    m[mod.key] = { ver: false, crear: false, editar: false, eliminar: false };
  });
  return m;
};

const RolesPage = () => {
  const { api, user } = useAuth();
  const themeColors = useThemeColors();
  const puedeCrear = tienePermiso(user, 'roles', 'crear');
  const puedeEditar = tienePermiso(user, 'roles', 'editar');
  const puedeEliminar = tienePermiso(user, 'roles', 'eliminar');
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [acciones, setAcciones] = useState(['ver', 'crear', 'editar', 'eliminar']);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ nombre: '', descripcion: '', permisos: {} });

  const fetchData = async (signal) => {
    setLoading(true);
    try {
      const [rolesRes, modRes] = await Promise.all([
        api.get('/roles', { signal }),
        api.get('/roles/modulos', { signal }),
      ]);
      setRoles(rolesRes.data || []);
      setModulos(modRes.data?.modulos || []);
      setAcciones(modRes.data?.acciones || ['ver', 'crear', 'editar', 'eliminar']);
    } catch (err) {
      console.error('Error fetching roles:', err);
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => fetchData(signal), []);

  const openCreate = () => {
    setEditing(null);
    setReadOnly(false);
    setForm({ nombre: '', descripcion: '', permisos: emptyMatrix(modulos) });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (rol, viewOnly = false) => {
    setEditing(rol);
    setReadOnly(viewOnly || rol.es_sistema);
    setForm({
      nombre: rol.nombre,
      descripcion: rol.descripcion || '',
      permisos: { ...emptyMatrix(modulos), ...(rol.permisos || {}) },
    });
    setError('');
    setDialogOpen(true);
  };

  const togglePerm = (modKey, accion) => {
    if (readOnly) return;
    setForm((prev) => ({
      ...prev,
      permisos: {
        ...prev.permisos,
        [modKey]: { ...prev.permisos[modKey], [accion]: !prev.permisos[modKey]?.[accion] },
      },
    }));
  };

  const toggleModuloRow = (modKey, value) => {
    if (readOnly) return;
    setForm((prev) => ({
      ...prev,
      permisos: {
        ...prev.permisos,
        [modKey]: acciones.reduce((acc, a) => { acc[a] = value; return acc; }, {}),
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setError('');
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        permisos: form.permisos,
      };
      if (editing) {
        await api.put(`/roles/${editing.id}`, payload);
      } else {
        await api.post('/roles', payload);
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el rol');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rol) => {
    if (rol.es_sistema) return;
    if (!window.confirm(`¿Eliminar el rol "${rol.nombre}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/roles/${rol.id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al eliminar el rol');
    }
  };

  const countVisibles = (permisos) =>
    Object.values(permisos || {}).filter((p) => p?.ver).length;

  // Agrupar módulos por grupo para la matriz
  const grupos = modulos.reduce((acc, m) => {
    (acc[m.grupo] = acc[m.grupo] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="roles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Roles y Permisos</h1>
          <p className="text-slate-500 text-sm mt-1">
            Define roles y controla qué módulos ve cada usuario en el menú lateral.
          </p>
        </div>
        {puedeCrear && (
          <Button
            onClick={openCreate}
            data-testid="crear-rol-btn"
            className="text-white"
            {...getPrimaryButtonStyles(themeColors)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Rol
          </Button>
        )}
      </div>

      {loading ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {roles.map((rol) => (
            <Card
              key={rol.id}
              className="border-slate-200 hover:shadow-md transition-shadow"
              data-testid={`rol-card-${rol.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                      <ShieldCheck className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{rol.nombre}</p>
                      {rol.es_sistema ? (
                        <Badge variant="outline" className="text-[10px] mt-0.5 gap-1">
                          <Lock className="w-3 h-3" /> Sistema
                        </Badge>
                      ) : (
                        <Badge className="text-[10px] mt-0.5 bg-slate-100 text-slate-700">Personalizado</Badge>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-sm text-slate-500 mt-3 min-h-[40px]">
                  {rol.descripcion || 'Sin descripción'}
                </p>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <span className="text-xs text-slate-500">
                    {countVisibles(rol.permisos)} módulos visibles · {rol.usuarios_count} usuario(s)
                  </span>
                  <div className="flex gap-1">
                    {(rol.es_sistema || !puedeEditar) ? (
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => openEdit(rol, true)}
                        data-testid={`ver-rol-${rol.id}`}
                        title="Ver permisos"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => openEdit(rol)}
                        data-testid={`editar-rol-${rol.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                    {!rol.es_sistema && puedeEliminar && (
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => handleDelete(rol)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`eliminar-rol-${rol.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog crear/editar/ver */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />
              {readOnly ? 'Permisos del Rol' : editing ? 'Editar Rol' : 'Nuevo Rol'}
            </DialogTitle>
            <DialogDescription>
              Configura el nombre y la matriz de permisos por módulo. La columna "Ver" define qué se muestra en el menú lateral.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {readOnly && (
              <Alert>
                <Lock className="h-4 w-4" />
                <AlertDescription>Este es un rol de sistema y no se puede modificar.</AlertDescription>
              </Alert>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="rol-nombre">Nombre *</Label>
                <Input
                  id="rol-nombre"
                  data-testid="rol-nombre-input"
                  value={form.nombre}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Ej: Auditor Interno"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="rol-desc">Descripción</Label>
                <Input
                  id="rol-desc"
                  data-testid="rol-descripcion-input"
                  value={form.descripcion}
                  disabled={readOnly}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Breve descripción del rol"
                />
              </div>
            </div>

            {/* Matriz de permisos */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1fr_repeat(4,64px)] items-center bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                <span>Módulo</span>
                {acciones.map((a) => (
                  <span key={a} className="text-center">{a}</span>
                ))}
              </div>
              <div className="divide-y divide-slate-100">
                {Object.entries(grupos).map(([grupo, mods]) => (
                  <div key={grupo}>
                    <div className="px-3 py-1.5 bg-slate-100/60 text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                      {grupo}
                    </div>
                    {mods.map((mod) => (
                      <div
                        key={mod.key}
                        className="grid grid-cols-[1fr_repeat(4,64px)] items-center px-3 py-2 hover:bg-slate-50"
                      >
                        <button
                          type="button"
                          disabled={readOnly}
                          onClick={() => toggleModuloRow(mod.key, !form.permisos[mod.key]?.ver)}
                          className="text-left text-sm text-slate-700 disabled:cursor-default"
                          title="Alternar todo el módulo"
                        >
                          {mod.label}
                        </button>
                        {acciones.map((a) => (
                          <div key={a} className="flex justify-center">
                            <Checkbox
                              checked={!!form.permisos[mod.key]?.[a]}
                              disabled={readOnly}
                              onCheckedChange={() => togglePerm(mod.key, a)}
                              data-testid={`perm-${mod.key}-${a}`}
                            />
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <p className="text-xs text-slate-400">
              La columna <strong>Ver</strong> controla la visibilidad del módulo en el menú lateral.
              Crear / Editar / Eliminar se guardan para aplicarse próximamente.
            </p>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {readOnly ? 'Cerrar' : 'Cancelar'}
              </Button>
              {!readOnly && (
                <Button
                  type="submit"
                  disabled={saving}
                  data-testid="guardar-rol-btn"
                  className="text-white"
                  {...getPrimaryButtonStyles(themeColors)}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editing ? 'Actualizar' : 'Crear'}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RolesPage;
